/**
 * Helpers for rendering user-entered free text in vue-good-table cells.
 * Long unbroken strings are truncated more aggressively than spaced text.
 */

export const USER_TEXT_DATATABLE_FIELDS = new Set([
  'name',
  'description',
  'email',
  'phone',
  'notes',
  'title',
  'customer_name',
  'supervisor_name',
  'supervisor',
  'package_name',
  'unit',
  'unit_name',
  'time_interval',
  'product_name',
  'user_name',
  'supplier_name',
])

export const SYSTEM_DATATABLE_FIELDS = new Set([
  'action',
  'amount',
  'amount_refunded',
  'additional_price',
  'bookings',
  'created_at',
  'date',
  'deposit',
  'from_date',
  'expire_date',
  'id',
  'image',
  'inactive',
  'method',
  'mobile_view',
  'paid',
  'payment_status',
  'picture',
  'pickup',
  'platform',
  'price',
  'product_image',
  'quantity',
  'refunds',
  'role',
  'service_category_id',
  'parent_category_id',
  'parent_location_id',
  'status',
  'stripe_id',
  'survey',
  'time',
  'total',
  'total_stock',
  'type',
  'user',
])

export function isUserTextDatatableField(field) {
  if (!field) {
    return false
  }
  if (SYSTEM_DATATABLE_FIELDS.has(field)) {
    return false
  }
  return USER_TEXT_DATATABLE_FIELDS.has(field)
}

export const DEFAULT_DATATABLE_TEXT_LIMITS = {
  maxLength: 30,
  noSpaceMaxLength: 20,
}

export const FIELD_DATATABLE_TEXT_LIMITS = {
  name: {
    maxLength: 40,
    noSpaceMaxLength: 20,
  },
  description: {
    maxLength: 150,
    noSpaceMaxLength: 20,
  },
  product_name: {
    maxLength: 40,
    noSpaceMaxLength: 20,
  },
  unit: {
    maxLength: 40,
    noSpaceMaxLength: 20,
  },
  unit_name: {
    maxLength: 40,
    noSpaceMaxLength: 20,
  },
  package_name: {
    maxLength: 40,
    noSpaceMaxLength: 20,
  },
  email: {
    maxLength: 40,
    noSpaceMaxLength: 40,
  },
}

export function getDatatableUserTextOptions(field, overrides = {}) {
  const fieldLimits = field && FIELD_DATATABLE_TEXT_LIMITS[field]
    ? FIELD_DATATABLE_TEXT_LIMITS[field]
    : {}

  return {
    ...DEFAULT_DATATABLE_TEXT_LIMITS,
    ...fieldLimits,
    ...overrides,
  }
}

export function formatDatatableUserText(value, options = {}) {
  const {
    maxLength = DEFAULT_DATATABLE_TEXT_LIMITS.maxLength,
    noSpaceMaxLength = DEFAULT_DATATABLE_TEXT_LIMITS.noSpaceMaxLength,
  } = options

  const text = value === null || value === undefined ? '' : String(value).trim()
  if (!text) {
    return { display: '', tooltip: '' }
  }

  const segments = text.split(/\s+/).filter(Boolean)
  const longestSegment = segments.length
    ? Math.max(...segments.map((segment) => segment.length))
    : text.length

  let limit = maxLength
  if (!/\s/.test(text) || longestSegment > noSpaceMaxLength) {
    limit = noSpaceMaxLength
  }

  if (text.length <= limit) {
    return { display: text, tooltip: '' }
  }

  return {
    display: `${text.slice(0, limit)}...`,
    tooltip: text,
  }
}

export function datatableUserTextDisplay(value, options) {
  return formatDatatableUserText(value, options).display
}

export function datatableUserTextTooltip(value, options) {
  return formatDatatableUserText(value, options).tooltip
}
